import React, { Fragment } from 'react';
import { Container, FormGroup } from 'reactstrap';
import { States } from '@/helpers/common/states';
import { Button, InputText, Loading, SelectCustom } from '@/components';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { FormInputName, InstallmentsFeeType, InstallmentFeeForm } from '../../types';

interface StateProps {
  state: States;
  formData: FormData;
  formErrors: FormErrors;
  ticketPhysicalSaleInstallment: InstallmentFeeForm[];
  ticketWebsiteSaleInstallment: InstallmentFeeForm[];
  productPhysicalSaleInstallment: InstallmentFeeForm[];
  productWebsiteSaleInstallment: InstallmentFeeForm[];
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onChangeInstallments: (type: InstallmentsFeeType, value: number) => void;
  onChangeFee: (index: number, type: InstallmentsFeeType, value: string) => void;
  onSave: () => void;
}

type Props = StateProps & DispatchProps;

export const CardFeeContainer: React.FC<Props> = ({
  state,
  formData,
  onChangeFormInput,
  formErrors,
  ticketPhysicalSaleInstallment,
  ticketWebsiteSaleInstallment,
  productPhysicalSaleInstallment,
  productWebsiteSaleInstallment,
  onChangeInstallments,
  onChangeFee,
  onSave,
}): JSX.Element => {
  const renderInstallments = (
    type: InstallmentsFeeType,
    items: InstallmentFeeForm[],
  ): JSX.Element => {
    const field = `${type}Installments`;
    const maximum = Math.max(12, Number(formData[field]) || 0);
    return (
      <>
        <SelectCustom
          name={field}
          label="Qtd parcelas"
          className="w-input-sm"
          value={formData[field]}
          onChange={option => onChangeInstallments(type, Number(option?.value ?? 0))}
          error={formErrors[field]?.[0]}
          options={Array.from({ length: maximum + 1 }, (_, index) => ({
            label: index === 0 ? 'Sem parcelas' : String(index),
            value: String(index),
          }))}
        />
        {items.map((item, index) => (
          <div className="d-flex mb-3" key={`${type}-${item.installment}`}>
            <InputText
              name={`${type}Installment-${item.installment}`}
              label="Parcelas"
              className="w-input-sm"
              addon="x"
              value={String(item.installment)}
              onChange={() => undefined}
              disabled
            />
            <div className="ml-3">
              <InputText
                name={`${type}Fee-${item.installment}`}
                label="Juros ao mês"
                className="w-input-sm"
                addon="%"
                maxLength={5}
                value={item.fee}
                onChange={e => onChangeFee(index, type, e.target.value)}
                error={item.fee.trim() === '' ? 'Informe a taxa da parcela' : undefined}
              />
            </div>
          </div>
        ))}
      </>
    );
  };
  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Container className="mainContainer" fluid={true}>
        <div className="container-event mb-4">
          <h5 className="mb-2 border-bottom-title mb-5">Taxas dos Ingressos</h5>

          <p style={{ fontSize: '21px', fontWeight: '500' }}>Venda física</p>

          <FormGroup>
            <InputText
              name="ticketPhysicalSaleDebit"
              label="Débito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketPhysicalSaleDebit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketPhysicalSaleDebit)(e.target.value)
              }
              error={formErrors.ticketPhysicalSaleDebit && formErrors.ticketPhysicalSaleDebit[0]}
            />
            <InputText
              name="ticketPhysicalSaleCredit"
              label="Crédito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketPhysicalSaleCredit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketPhysicalSaleCredit)(e.target.value)
              }
              error={formErrors.ticketPhysicalSaleCredit && formErrors.ticketPhysicalSaleCredit[0]}
            />
            <InputText
              name="ticketPhysicalSalePix"
              label="PIX"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketPhysicalSalePix]}
              placeholder="Ex: 1"
              onChange={e => onChangeFormInput(FormInputName.ticketPhysicalSalePix)(e.target.value)}
              error={formErrors.ticketPhysicalSalePix && formErrors.ticketPhysicalSalePix[0]}
            />
            <InputText
              name="ticketPhysicalSaleAdministrateTax"
              label="Taxa administrativa"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketPhysicalSaleAdministrateTax]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketPhysicalSaleAdministrateTax)(e.target.value)
              }
              error={
                formErrors.ticketPhysicalSaleAdministrateTax &&
                formErrors.ticketPhysicalSaleAdministrateTax[0]
              }
            />
          </FormGroup>

          {renderInstallments(
            InstallmentsFeeType.ticketPhysicalSale,
            ticketPhysicalSaleInstallment,
          )}

          <p style={{ fontSize: '21px', fontWeight: '500' }}>Venda e-commerce</p>

          <FormGroup>
            <InputText
              name="ticketWebsiteSaleBankSlip"
              label="Boleto"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketWebsiteSaleBankSlip]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketWebsiteSaleBankSlip)(e.target.value)
              }
              error={
                formErrors.ticketWebsiteSaleBankSlip && formErrors.ticketWebsiteSaleBankSlip[0]
              }
            />
            <InputText
              name="ticketWebsiteSaleCredit"
              label="Crédito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketWebsiteSaleCredit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketWebsiteSaleCredit)(e.target.value)
              }
              error={formErrors.ticketWebsiteSaleCredit && formErrors.ticketWebsiteSaleCredit[0]}
            />
            <InputText
              name="ticketWebsiteSalePix"
              label="PIX"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketWebsiteSalePix]}
              placeholder="Ex: 1"
              onChange={e => onChangeFormInput(FormInputName.ticketWebsiteSalePix)(e.target.value)}
              error={formErrors.ticketWebsiteSalePix && formErrors.ticketWebsiteSalePix[0]}
            />
            <InputText
              name="ticketWebsiteSaleAdministrateTax"
              label="Taxa administrativa"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.ticketWebsiteSaleAdministrateTax]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.ticketWebsiteSaleAdministrateTax)(e.target.value)
              }
              error={
                formErrors.ticketWebsiteSaleAdministrateTax &&
                formErrors.ticketWebsiteSaleAdministrateTax[0]
              }
            />
          </FormGroup>

          {renderInstallments(InstallmentsFeeType.ticketWebsiteSale, ticketWebsiteSaleInstallment)}
        </div>

        <div className="container-event mb-4">
          <h5 className="mb-2 border-bottom-title mb-5">Taxas dos Produtos</h5>

          <p style={{ fontSize: '21px', fontWeight: '500' }}>Venda física</p>

          <FormGroup>
            <InputText
              name="productPhysicalSaleDebit"
              label="Débito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productPhysicalSaleDebit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productPhysicalSaleDebit)(e.target.value)
              }
              error={formErrors.productPhysicalSaleDebit && formErrors.productPhysicalSaleDebit[0]}
            />
            <InputText
              name="productPhysicalSaleCredit"
              label="Crédito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productPhysicalSaleCredit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productPhysicalSaleCredit)(e.target.value)
              }
              error={
                formErrors.productPhysicalSaleCredit && formErrors.productPhysicalSaleCredit[0]
              }
            />
            <InputText
              name="productPhysicalSalePix"
              label="PIX"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productPhysicalSalePix]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productPhysicalSalePix)(e.target.value)
              }
              error={formErrors.productPhysicalSalePix && formErrors.productPhysicalSalePix[0]}
            />
            <InputText
              name="productPhysicalSaleAdministrateTax"
              label="Taxa administrativa"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productPhysicalSaleAdministrateTax]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productPhysicalSaleAdministrateTax)(e.target.value)
              }
              error={
                formErrors.productPhysicalSaleAdministrateTax &&
                formErrors.productPhysicalSaleAdministrateTax[0]
              }
            />
          </FormGroup>
          <FormGroup>
            {renderInstallments(
              InstallmentsFeeType.productPhysicalSale,
              productPhysicalSaleInstallment,
            )}
          </FormGroup>

          <p style={{ fontSize: '21px', fontWeight: '500' }}>Venda e-commerce</p>

          <FormGroup>
            <InputText
              name="productWebsiteSaleBankSlip"
              label="Boleto"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productWebsiteSaleBankSlip]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productWebsiteSaleBankSlip)(e.target.value)
              }
              error={
                formErrors.productWebsiteSaleBankSlip && formErrors.productWebsiteSaleBankSlip[0]
              }
            />
            <InputText
              name="productWebsiteSaleCredit"
              label="Crédito"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productWebsiteSaleCredit]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productWebsiteSaleCredit)(e.target.value)
              }
              error={formErrors.productWebsiteSaleCredit && formErrors.productWebsiteSaleCredit[0]}
            />
            <InputText
              name="productWebsiteSalePix"
              label="PIX"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productWebsiteSalePix]}
              placeholder="Ex: 1"
              onChange={e => onChangeFormInput(FormInputName.productWebsiteSalePix)(e.target.value)}
              error={formErrors.productWebsiteSalePix && formErrors.productWebsiteSalePix[0]}
            />
            <InputText
              name="productWebsiteSaleAdministrateTax"
              label="Taxa administrativa"
              className="w-input-sm"
              addon="%"
              maxLength={5}
              value={formData[FormInputName.productWebsiteSaleAdministrateTax]}
              placeholder="Ex: 1"
              onChange={e =>
                onChangeFormInput(FormInputName.productWebsiteSaleAdministrateTax)(e.target.value)
              }
              error={
                formErrors.productWebsiteSaleAdministrateTax &&
                formErrors.productWebsiteSaleAdministrateTax[0]
              }
            />
          </FormGroup>

          {renderInstallments(
            InstallmentsFeeType.productWebsiteSale,
            productWebsiteSaleInstallment,
          )}
        </div>
        <hr className="mt-5" />
        <div className="footer-register-event">
          <Button title="Salvar" onClick={() => onSave()} />
        </div>
      </Container>
    </Fragment>
  );
};
